// Adapted from eval/dashboard/internal_web/src/components/session/Transcript.tsx (ATM-57): live turn rows, no reviewer notes. Keep in step until ATM-60 shares it.
/**
 * The consultation itself, read top to bottom — the eval dashboard's `Transcript`, on a live
 * session's rows.
 *
 * Bubbles, not rows: Jiva on the cool grey surface, the patient on the warm one, so the two
 * sides read apart before a word is read, with the speaker named in the left gutter. The
 * measure is capped at ~62 characters even though the page is wide — a column a human can
 * actually read.
 *
 * Each turn row is one exchange: Jiva's words as they were SPOKEN on the call (`turnDialogue`),
 * then the patient's answer. med-reasoning's own template and the options it attached are
 * internals: they hang off the line beneath Jiva's words, on hover, never inside the bubble
 * (see `SourceQuestionMark`). A row from before the spoken dialogue was stored carries only the
 * answer; there the template stands in for the question, and says so. The patient's answer is
 * the voice model's summary, not Whisper's transcription (on hover), which is often garbled.
 *
 * An **inferred** row is a question med-reasoning composed and then answered from what it
 * already knew. Its words are a question, so unmarked it reads as an exchange that happened;
 * it is marked above a dashed bubble instead, with med-reasoning's reasoning beneath it.
 *
 * Each exchange is the unit `useAnchoredTurn` observes (`data-agent-turn`), and the one the
 * differential panel beside the transcript is about is boxed.
 */

import type { ReactNode } from "react";

import type { TurnView } from "../../api/types";
import { cn } from "../../lib/cn";
import { relationWords } from "../../lib/relations";
import { parseTurnDialogue, type Utterance } from "./turnDialogue";
import { AGENT_TURN_ATTR } from "./useAnchoredTurn";

/** What an inferred row holds as its answer when med-reasoning gave no reasoning. */
const INFERRED_PLACEHOLDER = "(inferred from prior turns)";

/** An utterance as shown, with an optional hover note on its bubble. */
type Shown = Utterance & { note?: string };

type Props = {
  /** The session's rows, in turn order. */
  turns: readonly TurnView[];
  /** The exchange the differential panel is anchored beside; `null` boxes none. */
  anchoredTurn: number | null;
  /** The newest answer, while its differential is still being stored. */
  pendingTurn: number | null;
  /** The patient's recorded introduction (`lib/patient`), shown as their first answer. */
  intro: string | null;
  /** Whether the session is still running: the transcript then ends on a live tail. */
  active: boolean;
  className?: string;
};

/**
 * Where a question came from: med-reasoning's template, and the options it attached. Kept to
 * a hover rather than a row because a doctor is judging the question the patient was asked —
 * the template only matters when the phrasing looks wrong.
 *
 * The options are labelled as never spoken, because that is the fact most likely to be
 * misread: they are private examples the voice agent is told not to read out.
 */
function SourceQuestionMark({
  questionText,
  options,
}: {
  questionText: string;
  options: readonly string[];
}) {
  const tooltip = [
    `med-reasoning asked: ${questionText}`,
    options.length > 0
      ? `Options attached (private to the voice agent, never spoken): ${options.join(", ")}`
      : null,
  ]
    .filter((line) => line !== null)
    .join("\n\n");
  return (
    <span title={tooltip} className="cursor-help underline decoration-dotted underline-offset-2">
      template
    </span>
  );
}

/**
 * A row of `(relation, values)` chips: what the mapper took an answer to mean, or what
 * med-reasoning filled in on an inferred row. The caller's `title` says which.
 */
function FactorChips({ factors, title }: { factors: Record<string, string[]>; title: string }) {
  const rows = Object.entries(factors).filter(([, values]) => values.length > 0);
  if (rows.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-1.5" title={title}>
      {rows.map(([relation, values]) => (
        <li
          key={relation}
          className="rounded-pill border border-hairline-2 bg-surface px-2 py-[2px] text-tag text-ink-3"
        >
          <span className="text-ink-faint">{relationWords(relation)}</span> {values.join(", ")}
        </li>
      ))}
    </ul>
  );
}

/** The options med-reasoning attached, as strings; the column is free-form JSON. */
function optionsOf(turn: TurnView): string[] {
  return (turn.question_options ?? []).filter(
    (option): option is string => typeof option === "string",
  );
}

/** One utterance: the speaker in the gutter, the words in a bubble, and what hangs below. */
function Line({
  speaker,
  children,
  dashed = false,
  above,
  below,
  title,
}: {
  speaker: Utterance["speaker"];
  children: string;
  dashed?: boolean;
  above?: ReactNode;
  below?: ReactNode;
  /** A hover note on the bubble. */
  title?: string;
}) {
  const isAgent = speaker === "agent";
  return (
    <li className="flex items-start gap-3">
      <span className="w-20 shrink-0 pt-3 text-right text-tag text-ink-faint">
        {isAgent ? "Jiva" : "Patient"}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        {above}
        <div
          title={title}
          className={cn(
            "w-fit max-w-[62ch] whitespace-pre-wrap rounded-block border px-4 py-2.5 text-body text-ink",
            dashed
              ? "border-dashed border-hairline-agent bg-surface-agent"
              : isAgent
                ? "border-hairline-agent bg-surface-agent"
                : "border-hairline-patient bg-surface-patient",
          )}
        >
          {children}
        </div>
        {below}
      </div>
    </li>
  );
}

/** An inferred row: marked, dashed, with what med-reasoning filled in and why. */
function InferredExchange({ turn }: { turn: TurnView }) {
  const reasoning = (turn.selected_option ?? "").trim();
  return (
    <Line
      speaker="agent"
      dashed
      above={
        <p
          className="text-tag font-medium text-ink-faint"
          title="med-reasoning composed this question, found the answer already followed from earlier turns, and advanced its graph without asking."
        >
          Inferred · never asked
        </p>
      }
      below={
        <>
          <FactorChips
            factors={turn.learned_this_turn}
            title="What med-reasoning filled in on the patient's behalf, in the same shape as the mapper's own output."
          />
          {reasoning !== "" && reasoning !== INFERRED_PLACEHOLDER ? (
            <p className="max-w-[62ch] text-caption text-ink-3">
              Answered without asking: {reasoning}
            </p>
          ) : null}
        </>
      }
    >
      {turn.question_text}
    </Line>
  );
}

/** A spoken exchange: Jiva's words, the patient's, and what the answer was taken to mean. */
function SpokenExchange({
  turn,
  pending,
  intro,
}: {
  turn: TurnView;
  pending: boolean;
  /** The patient's recorded name, age and sex, answering Jiva's first question in turn 0. */
  intro: string | null;
}) {
  const dialogue = parseTurnDialogue(turn.selected_option);
  const opening = turn.turn_number === 0;
  // A row from before the spoken dialogue was stored holds only the answer: the template
  // stands in for the question, so the exchange still reads as one.
  const templateStandsIn = !dialogue.spoken && !opening && turn.question_text.trim() !== "";
  const stored: Utterance[] = templateStandsIn
    ? [{ speaker: "agent", text: turn.question_text }, ...dialogue.utterances]
    : dialogue.utterances;
  // Whisper's transcription of the patient is unreliable (wrong language, misheard words) and
  // can land after the row is stored, so the answers shown are the voice model's own: Jiva's
  // lines, then one patient bubble with its summary, which is what the mapper read and always
  // in the row (it comes with the tool call). The opening turn also answers Jiva's first
  // question with the name, age and sex the voice model recorded. Whisper's words stay on hover.
  const heard = stored.filter((utterance) => utterance.speaker === "patient");
  const agent = stored.filter((utterance) => utterance.speaker === "agent");
  const transcribedAs = (words: readonly Utterance[]) =>
    words.length > 0
      ? `Transcribed: ${words.map((utterance) => utterance.text).join(" / ")}`
      : "The patient's words were not transcribed.";
  const introduced: Shown[] =
    opening && intro !== null && agent.length > 1
      ? [
          agent[0],
          {
            speaker: "patient",
            text: intro,
            note: `What the voice model recorded from the introduction. ${transcribedAs(heard.slice(0, 1))}`,
          },
          ...agent.slice(1),
        ]
      : agent;
  const utterances: Shown[] =
    dialogue.summary === null
      ? stored
      : [
          ...introduced,
          {
            speaker: "patient",
            text: dialogue.summary,
            note: `The voice model's summary of the answer. ${transcribedAs(
              introduced.length > agent.length ? heard.slice(1) : heard,
            )}`,
          },
        ];

  // The meta line hangs off Jiva's last words before the patient first answers.
  const firstAnswer = utterances.findIndex((utterance) => utterance.speaker === "patient");
  const leading = firstAnswer === -1 ? utterances : utterances.slice(0, firstAnswer);
  const metaAt = leading.map((utterance) => utterance.speaker).lastIndexOf("agent");

  const meta = opening ? null : templateStandsIn ? (
    <p className="text-tag text-ink-faint">
      med-reasoning&rsquo;s question — the spoken wording was not stored for this exchange
    </p>
  ) : (
    <p className="text-tag text-ink-faint">
      <SourceQuestionMark questionText={turn.question_text} options={optionsOf(turn)} />
    </p>
  );

  const extras = (
    <>
      <FactorChips
        factors={turn.learned_this_turn}
        title="What the transcript→factor mapper took from this answer."
      />
      {turn.clarification_text !== null && turn.clarification_text.trim() !== "" ? (
        <p className="max-w-[62ch] text-caption text-ink-3">
          <span className="text-ink-faint">Clarification:</span> &ldquo;{turn.clarification_text}
          &rdquo;
        </p>
      ) : null}
      {pending ? (
        <p className="text-caption text-ink-3" role="status">
          Updating the differential after this answer…
        </p>
      ) : null}
    </>
  );

  if (utterances.length === 0) {
    return (
      <li className="flex items-start gap-3">
        <span className="w-20 shrink-0" aria-hidden />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <p className="text-caption text-ink-faint">No words were stored for this exchange.</p>
          {extras}
        </div>
      </li>
    );
  }

  const last = utterances.length - 1;
  return (
    <>
      {utterances.map((utterance, position) => (
        <Line
          key={position}
          speaker={utterance.speaker}
          below={
            <>
              {position === metaAt ? meta : null}
              {position === last ? extras : null}
            </>
          }
          title={utterance.note}
        >
          {utterance.text}
        </Line>
      ))}
    </>
  );
}

/** The session's exchanges, the anchored one boxed, and a live tail while it runs. */
export default function Transcript({
  turns,
  anchoredTurn,
  pendingTurn,
  intro,
  active,
  className,
}: Props) {
  return (
    <ol className={cn("flex flex-col", className)}>
      {turns.map((turn) => {
        const anchored = turn.turn_number === anchoredTurn;
        return (
          <li key={turn.turn_number} {...{ [AGENT_TURN_ATTR]: turn.turn_number }}>
            {/* The exchange the panel is about: boxed all round and tinted, so it reads from
                across the page. One element either way, only its colours toggled, so moving
                the anchor never remounts an exchange or shifts it a pixel. */}
            <div
              aria-current={anchored ? "step" : undefined}
              className={cn(
                "rounded-panel border p-3 transition-colors duration-300 motion-reduce:transition-none",
                anchored ? "border-hairline-2 bg-surface-muted" : "border-transparent",
              )}
            >
              <ol className="flex flex-col gap-5">
                {turn.is_inferred ? (
                  <InferredExchange turn={turn} />
                ) : (
                  <SpokenExchange
                    turn={turn}
                    pending={turn.turn_number === pendingTurn}
                    intro={intro}
                  />
                )}
              </ol>
            </div>
          </li>
        );
      })}
      {active ? (
        // Room below the newest exchange, so it can reach the reading band the panel follows,
        // and the one line on the page that says the consultation is still going.
        <li className="flex min-h-[45vh] items-start gap-3 p-3" aria-live="polite">
          <span className="w-20 shrink-0" aria-hidden />
          <p className="flex items-center gap-2 text-caption text-ink-faint">
            <span aria-hidden className="h-1.5 w-1.5 rounded-pill bg-ink-strong" />
            {turns.length === 0
              ? "Waiting for the opening description — the first exchange appears once the patient has said what brought them in."
              : "In progress — the next exchange appears once the patient has answered."}
          </p>
        </li>
      ) : null}
    </ol>
  );
}
